import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { ApiError } from 'src/error/ApiError';
import { Logger } from 'src/utils/Logger';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import {
  SharepointReconnectRequiredError,
  useSharepointFolderPicker,
} from './useSharepointFolderPicker';
import { useUploadToSharepointMutation } from './useUploadToSharepointMutation';

const UPLOAD_PROGRESS_NOTIFICATION_ID = 'sharepoint-upload-progress';

type SharepointUploadItem = {
  fileId: number;
  fileName: string;
};

type UseSharepointUploadParams = {
  onComplete: () => void;
} & ({ clientId: string; familyId?: never } | { familyId: string; clientId?: never });

export function useSharepointUpload(params: UseSharepointUploadParams) {
  const { onComplete } = params;
  const clientId = 'clientId' in params ? params.clientId : undefined;
  const familyId = 'familyId' in params ? params.familyId : undefined;
  const logContext = { clientId, familyId };
  const [isUploadingToSharepoint, setIsUploadingToSharepoint] = useState(false);
  const { pickFolder, createUploadSession, acquireWriteAccess, isConnected, connect } =
    useSharepointFolderPicker();
  const { mutateAsync: uploadToSharepoint } = useUploadToSharepointMutation();

  useEffect(() => {
    if (!isUploadingToSharepoint) return;
    // Reloading or closing the tab aborts an in-flight upload, so warn the user first.
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isUploadingToSharepoint]);

  const startSharepointSignIn = async () => {
    try {
      await connect();
    } catch (err) {
      notifyAndLogError(
        'Could not connect to SharePoint',
        'We could not start the SharePoint sign-in. Please try again.',
        err,
        logContext,
      );
    }
  };

  const openPicker = async (items: SharepointUploadItem[]) => {
    if (items.length === 0 || isUploadingToSharepoint) return;

    if (!isConnected) {
      // No SharePoint account yet — send the user to sign in. MSAL returns them to
      // this page once connected, and they can start the upload again.
      await startSharepointSignIn();
      return;
    }

    let folder: Awaited<ReturnType<typeof pickFolder>>;
    try {
      folder = await pickFolder();
    } catch (err) {
      if (err instanceof BrowserAuthError && err.errorCode === 'user_cancelled') {
        // User dismissed the Microsoft sign-in prompt — nothing to upload, no error.
        return;
      }
      if (
        err instanceof InteractionRequiredAuthError ||
        err instanceof SharepointReconnectRequiredError
      ) {
        // Consent has lapsed (e.g. access revoked in Microsoft "My Apps"). The stale
        // cached account still reads as connected, so token acquisition either fails
        // silently (InteractionRequiredAuthError) or returns a dead token that Graph
        // rejects (SharepointReconnectRequiredError). Either way, start a fresh
        // interactive sign-in to re-consent rather than showing the misleading
        // "allow popups" message.
        await startSharepointSignIn();
        return;
      }
      notifyAndLogError(
        'Could not connect to SharePoint',
        'We could not connect to your SharePoint account. Please ensure popups are allowed and try again.',
        err,
        logContext,
      );
      return;
    }

    if (!folder) {
      // User cancelled the picker — no toast needed, just silently return.
      return;
    }

    const { driveId, folderId } = folder;

    // Acquire the write scope once for the whole batch. Doing this per file would
    // re-prompt for consent on every file if the user cancelled the popup.
    let writeToken: string;
    try {
      writeToken = await acquireWriteAccess();
    } catch (err) {
      if (err instanceof BrowserAuthError && err.errorCode === 'user_cancelled') {
        // User dismissed the consent prompt — nothing to upload, no error.
        return;
      }
      notifyAndLogError(
        'Could not connect to SharePoint',
        'We could not get permission to write to your SharePoint account. Please try again.',
        err,
        logContext,
      );
      return;
    }

    setIsUploadingToSharepoint(true);
    let insufficientScope = false;

    notifications.show({
      id: UPLOAD_PROGRESS_NOTIFICATION_ID,
      loading: true,
      autoClose: false,
      withCloseButton: false,
      title:
        items.length === 1
          ? 'Uploading to SharePoint'
          : `Uploading ${items.length} files to SharePoint`,
      message: 'Please wait while your files are uploaded.',
    });

    try {
      for (const item of items) {
        let uploadUrl: string;
        try {
          uploadUrl = await createUploadSession(driveId, folderId, item.fileName, writeToken);
        } catch (err) {
          notifyAndLogError(
            'Upload failed',
            `Failed to start upload session for "${item.fileName}". Please try again.`,
            err,
            { ...logContext, fileId: item.fileId },
          );
          continue;
        }

        try {
          await uploadToSharepoint(
            familyId
              ? { fileId: item.fileId, familyId, uploadUrl }
              : { fileId: item.fileId, clientId: clientId ?? '', uploadUrl },
          );
          notifications.show({
            color: 'green',
            title: 'File uploaded',
            message: `"${item.fileName}" was uploaded to SharePoint.`,
          });
        } catch (error) {
          // Status-only detection: fetchWrapper reads the 403 body to check for
          // SSO_EMAIL_MISMATCH before ApiError.fromApiResponse can parse the
          // SHAREPOINT_INSUFFICIENT_SCOPE code, so the structured code is not
          // readable here. A plain 403 is safe to treat as insufficient scope
          // because the upload endpoint is the only action that can produce a 403
          // for this user-initiated operation.
          if (error instanceof ApiError && error.status === 403) {
            insufficientScope = true;
            notifications.show({
              color: 'red',
              title: 'SharePoint reconnection required',
              message:
                'Your SharePoint account needs additional permissions. Please reconnect your SharePoint account and try again.',
            });
            Logger.warn('SharePoint upload blocked — insufficient scope', {
              ...logContext,
              fileId: item.fileId,
            });
            break;
          }

          notifyAndLogError(
            'Upload failed',
            `Failed to upload "${item.fileName}" to SharePoint. Please try again.`,
            error,
            { ...logContext, fileId: item.fileId },
          );
        }
      }
    } finally {
      notifications.hide(UPLOAD_PROGRESS_NOTIFICATION_ID);
      setIsUploadingToSharepoint(false);
    }

    if (!insufficientScope) {
      onComplete();
    }
  };

  return {
    openPicker,
    isUploadingToSharepoint,
  };
}
